import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../../../Styles/Admin/assessment/AssessmentLogin.css";
import API_BASE_URL from "../../../config";

const AssessmentAdminLoginPage = () => {
  const navigate = useNavigate();

  // the login page also opens at the top (incl. on refresh)
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);

  // "login" = Login page, "forgot" = Forgot Password page
  const [view, setView] = useState("login");

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotMessageType, setForgotMessageType] = useState(""); // "success" | "error"
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleAssessmentAdminLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ── Login (calls backend API) ────────────────────────────────────────────
  const handleAssessmentAdminLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API_BASE_URL}/api/assessment/auth/login`, {
        email: loginData.email,
        password: loginData.password,
      });

      console.log("API Response:", res.data);

      if (!res.data.token) {
        setError("Login failed: No token received");
        setLoading(false);
        return;
      }

      localStorage.setItem("adminToken", res.data.token);
      console.log("SAVED TOKEN:", localStorage.getItem("adminToken"));

      navigate("/admin/assessment/dashboard", { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.msg || "Login failed. Please try again.");
    }
    setLoading(false);
  };

  // ── Forgot Password (calls backend API) ─────────────────────────────────
  const handleAssessmentAdminForgotSubmit = async (e) => {
    e.preventDefault();

    if (!forgotEmail) {
      setForgotMessage("Please enter your email");
      setForgotMessageType("error");
      return;
    }

    setForgotLoading(true);
    setForgotMessage("");

    try {
      const res = await axios.post(`${API_BASE_URL}/api/assessment/auth/forgot-password`, {
        email: forgotEmail,
      });

      if (res.data.success) {
        setForgotMessage(res.data.message || "Reset link sent!");
        setForgotMessageType("success");
      } else {
        setForgotMessage(res.data.message || "Something went wrong");
        setForgotMessageType("error");
      }
    } catch (err) {
      setForgotMessage(err.response?.data?.message || "Something went wrong. Please try again.");
      setForgotMessageType("error");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setView("login");
    setForgotEmail("");
    setForgotMessage("");
    setForgotMessageType("");
  };

  return (
    <div className="assessment_adminlogin-container">
      {/* ---------------- LEFT SIDE ---------------- */}
      <div className="assessment_adminlogin-left">
        <div className="assessment_adminlogin-logo">
          <img
            src="/New_osw_logo.png"
            alt="Outstanding Workplaces Logo"
            className="assessment_adminlogin-logo-img"
          />
        </div>

        <div className="assessment_adminlogin-illustration">
          <img src="/Customerlogin_img.png" alt="Outstanding Workplaces" />
        </div>

        <div className="assessment_adminlogin-left-bottom">
          <h2>Welcome Back!</h2>
          <p>Sign in to your Assessment account</p>
        </div>
      </div>

      {/* ---------------- RIGHT SIDE ---------------- */}
      <div className="assessment_adminlogin-right">
        {view === "login" ? (
          <form className="assessment_adminlogin-form" onSubmit={handleAssessmentAdminLoginSubmit}>
            <h1 className="assessment_adminform-heading">Assessment Login</h1>

            <p className="assessment_adminform-subheading">
              Enter your credentials to access the report dashboard
            </p>

            <label className="assessment_adminform-label">User Email ID</label>

            <input
              type="email"
              name="email"
              className="assessment_adminform-input"
              placeholder="Enter your Email ID"
              value={loginData.email}
              onChange={handleAssessmentAdminLoginChange}
              required
            />

            <label className="assessment_adminform-label">Password</label>

            <div className="assessment_adminpassword-field">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="assessment_adminform-input"
                placeholder="Enter your Password"
                value={loginData.password}
                onChange={handleAssessmentAdminLoginChange}
                required
              />
              <img
                src="/Admin_log_eye.png"
                alt="Toggle password visibility"
                className="assessment_adminpassword-eye-icon"
                onClick={() => setShowPassword((prev) => !prev)}
              />
            </div>

            {error && <div className="assessment_adminform-error">{error}</div>}

            <button
              type="button"
              className="assessment_adminlink-btn assessment_adminforgot-link"
              onClick={() => setView("forgot")}
            >
              Forgot Password?
            </button>

            <button type="submit" className="assessment_adminprimary-btn" disabled={loading}>
              {loading ? "SIGNING IN..." : "SIGN IN"}
            </button>
          </form>
        ) : (
          <form className="assessment_adminlogin-form" onSubmit={handleAssessmentAdminForgotSubmit}>
            <h1 className="assessment_adminform-heading">Forgot Password?</h1>

            <p className="assessment_adminform-subheading">
              Enter your email and we'll send a reset link to the registered
              email.
            </p>

            <label className="assessment_adminform-label">User Email ID</label>

            <input
              type="email"
              name="forgotEmail"
              className="assessment_adminform-input"
              placeholder="Enter your Email ID"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
              autoFocus
            />

            {forgotMessage && (
              <p
                className="assessment_admin-forgot-message"
                style={{
                  color: forgotMessageType === "success" ? "#16a34a" : "#dc2626",
                  fontSize: "13px",
                  marginBottom: "12px",
                  textAlign: "center",
                }}
              >
                {forgotMessage}
              </p>
            )}

            <button
              type="button"
              className="assessment_adminlink-btn assessment_adminback-link"
              onClick={handleBackToLogin}
            >
              <div className="assessment_adminback-link-content">
                <img
                  src="/Admin_log-arrow.png"
                  alt="Back"
                  className="assessment_adminback-arrow"
                />
                <span>Back to Login</span>
              </div>
            </button>

            <button
              type="submit"
              className="assessment_adminprimary-btn"
              disabled={forgotLoading}
              style={{ opacity: forgotLoading ? 0.7 : 1 }}
            >
              {forgotLoading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AssessmentAdminLoginPage;